import {
  Box,
  Button,
  Icon,
  Text,
  Flex,
  TextArea,
  Popover,
} from "orcs-design-system";
import React, {
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { useActiveTenant } from "src/contexts/global/ApolloSettingsContext";
import { useThemeSwitchingContext } from "src/contexts/ThemeSwitchingContext";
import Url, { url } from "src/consts/urlPaths";
import ErrorNotification from "../../ErrorNotification";
import { AILoader } from "../AILoader";
import { AIWarning } from "../AIWarning";
import AIIcon from "../AI_Icon";
import { AGENTCORE_NAMES } from "../consts";
import ChatHistoryPanel from "../ChatHistoryPanel";
import { AIChatV3Messages } from "./AIChatV3Messages";
import { useAIChatV3 } from "./AIChatV3Context";
import { CHAT_PHASE } from "./hooks/useAIChatPhase";

/**
 * v3 AI Chat Interface — clean rewrite for v2 agents.
 *
 * All lifecycle state derived from `chatPhase` via the state machine.
 * Agent selection is lifted to the Window; passed through context.
 */
const AIChatV3Interface = ({
  variant = "sidebar",
  showCloseButton = true,
  onClose,
  onChatSelect,
  headerTitle = "Ask TeamForm",
  showChatHistoryToggle = true,
  showNewChatButton = true,
}) => {
  const {
    canRetryOrEdit,
    editingMessageId,
    question,
    setQuestion,
    onSessionClear,
    chatPhase,
    isComplete,
    onSendQuestion,
    onStartNewChat,
    chats,
    chatId,
    chatsLoading,
    onSelectChat,
    error,
    setError,
    selectedAgent,
    setSelectedAgent,
    isSupportUser,
  } = useAIChatV3();

  const inputBoxRef = useRef();

  const showLoader =
    chatPhase === CHAT_PHASE.CONNECTING || chatPhase === CHAT_PHASE.THINKING;
  const isInputDisabled = !isComplete || (canRetryOrEdit && !editingMessageId);

  useEffect(() => {
    // Manually set textarea value due to orcs-design-system bug
    if (inputBoxRef.current) {
      inputBoxRef.current.value = question;
    }
  }, [question]);

  useEffect(() => {
    if (editingMessageId && inputBoxRef.current) {
      inputBoxRef.current.focus();
    }
  }, [editingMessageId]);

  const [showChatHistory, setShowChatHistory] = useState(false);
  const activeTenant = useActiveTenant();
  const { isDarkTheme } = useThemeSwitchingContext();
  const history = useHistory();

  const isFullPage = variant === "fullpage";

  const startNewChat = useCallback(() => {
    onSessionClear?.();
    onStartNewChat();
    setShowChatHistory(false);
  }, [onSessionClear, onStartNewChat]);

  const toggleChatHistory = useCallback(() => {
    setShowChatHistory((prev) => !prev);
  }, []);

  const handleSelectChat = useCallback(
    (selectedChatId) => {
      if (onChatSelect) {
        onChatSelect(selectedChatId);
      } else {
        onSelectChat(selectedChatId);
      }
      setShowChatHistory(false);
    },
    [onSelectChat, onChatSelect]
  );

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (activeTenant && !activeTenant?.canRunMutation) {
        setError(new Error("Readonly mode, cannot run mutations"));
        return;
      }

      if (inputBoxRef.current) {
        inputBoxRef.current.value = "";
      }
      onSendQuestion();
    },
    [onSendQuestion, activeTenant, setError]
  );

  useEffect(() => {
    if (inputBoxRef.current && isComplete) {
      inputBoxRef.current.focus();
    }
  }, [isComplete]);

  const headerButtons = useMemo(() => {
    const buttons = [];

    buttons.push({
      icon: ["fas", isFullPage ? "compress-arrows-alt" : "expand-arrows-alt"],
      label: isFullPage ? "View sidebar" : "View fullscreen",
      onClick: () => {
        if (isFullPage) {
          history.push(window.location.pathname);
        } else {
          history.push(`?aiViewMode=fullscreen`);
        }
      },
      hint: isFullPage ? "View sidebar" : "View fullscreen",
      active: !isFullPage,
    });

    if (selectedAgent === AGENTCORE_NAMES.ASK_TEAMFORM_V2) {
      buttons.push({
        icon: ["fas", "chart-bar"],
        label: "Build AI dashboard",
        onClick: () => {
          history.push(url(Url.AI_DASHBOARDS_V2, { dashboardId: "new" }));
        },
        hint: "Build AI dashboard",
      });
    }

    if (showChatHistoryToggle) {
      buttons.push({
        icon: ["far", "history"],
        label: "View chat history",
        onClick: toggleChatHistory,
        hint: "View chat history",
        active: showChatHistory,
      });
    }

    if (showNewChatButton) {
      buttons.push({
        icon: ["fas", "plus"],
        label: "Start new chat",
        onClick: startNewChat,
        hint: "Start a new chat",
      });
    }

    if (showCloseButton && onClose) {
      buttons.push({
        icon: ["fas", "times"],
        label: isFullPage ? "Back" : "Close chat",
        hint: isFullPage ? "Go back" : "Close chat",
        onClick: () => {
          onClose();
          history.push(window.location.pathname);
        },
      });
    }

    return buttons;
  }, [
    showChatHistoryToggle,
    showNewChatButton,
    showCloseButton,
    toggleChatHistory,
    startNewChat,
    onClose,
    showChatHistory,
    isFullPage,
    history,
    selectedAgent,
  ]);

  const containerStyles = isFullPage
    ? {
        width: "100%",
        height: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
      }
    : {};

  return (
    <Box
      overflow="hidden"
      height="100%"
      bg={isFullPage ? "transparent" : "surface"}
    >
      <Box
        height="inherit"
        bg="surface"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        borderRadius={isFullPage ? "8px" : "0"}
        borderLeft={!isFullPage ? "1px solid" : "none"}
        borderLeftColor={!isFullPage ? "surfaceBorder" : undefined}
        {...containerStyles}
      >
        {/* Header */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          padding="r"
          borderColor="surfaceBorder"
          background="surface"
          flexShrink={0}
          borderBottom="1px solid"
          borderBottomColor="surfaceBorder"
        >
          <Flex alignItems="center" gap="s">
            <AIIcon />
            {isSupportUser ? (
              <select
                value={selectedAgent}
                onChange={(e) => {
                  setSelectedAgent(e.target.value);
                  startNewChat();
                }}
                style={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "inherit",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  outline: "none",
                }}
              >
                <option value={AGENTCORE_NAMES.ASK_TEAMFORM_V2}>
                  Ask TeamForm v2
                </option>
                <option value={AGENTCORE_NAMES.ASK_TEAMFORM}>
                  Ask TeamForm
                </option>
                <option value={AGENTCORE_NAMES.GQL_EXPERT_V2}>
                  GQL Expert
                </option>
              </select>
            ) : (
              <Text fontWeight="600" fontSize="16px" color="bodyText">
                {headerTitle}
              </Text>
            )}
          </Flex>
          {headerButtons.length > 0 && (
            <Flex alignItems="center" gap="s">
              {headerButtons.map((button) => (
                <Popover
                  key={button.label}
                  text={button.hint}
                  direction="bottom"
                  width="fit-content"
                >
                  <Button
                    variant="ghost"
                    iconOnly
                    bg={button.active ? "primary" : "surfaceRaised"}
                    disabled={button.disabled || !isComplete}
                    opacity={button.disabled || !isComplete ? 0.5 : 1}
                    small
                    height="22px"
                    width="22px"
                    onClick={button.onClick}
                    ariaLabel={button.label}
                  >
                    <Icon
                      icon={button.icon}
                      color={button.active ? "white" : "subText"}
                    />
                  </Button>
                </Popover>
              ))}
            </Flex>
          )}
        </Flex>

        {showChatHistory ? (
          <ChatHistoryPanel
            chats={chats}
            currentChatId={chatId}
            onSelectChat={handleSelectChat}
            loading={chatsLoading}
          />
        ) : (
          <>
            {/* Chat Content */}
            <Box
              flex={1}
              minHeight={0}
              position="relative"
              paddingBottom={showLoader ? "30px" : "0"}
            >
              <AIChatV3Messages />
              {error && (
                <ErrorNotification
                  message={error?.message}
                  report={false}
                  onDismiss={() => setError(null)}
                />
              )}

              <Box
                padding="r"
                width="100%"
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                style={{ pointerEvents: "none" }}
              >
                <AILoader loading={showLoader} />
              </Box>
            </Box>

            {/* Input Area */}
            <Box
              padding="r"
              borderColor="surfaceBorder"
              background="surface"
              flexShrink={0}
              borderTop="1px solid"
              borderTopColor="surfaceBorder"
              paddingRight={isFullPage ? "0" : "calc(20% + 16px)"}
            >
              <AIWarning justifyContent="flex-start" mb="s" />

              <form onSubmit={onSubmit}>
                <Flex alignItems="center" gap="s">
                  <TextArea
                    id="ai-chat-question-input"
                    ariaLabel="Ask a question"
                    key={`textarea-${chatId}`}
                    onChange={(e) => setQuestion(e.target.value)}
                    disabled={isInputDisabled}
                    name="question"
                    onKeyDown={(e) => {
                      if (!question.trim()) {
                        return;
                      }
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.target.form.requestSubmit();
                      }
                    }}
                    resize="none"
                    fullWidth
                    ref={inputBoxRef}
                  >
                    {question}
                  </TextArea>
                  <Button
                    type="submit"
                    iconOnly
                    variant="dangerAlternate"
                    ariaLabel="Send message"
                    borderRadius="6px"
                    width={28}
                    height={28}
                    display="flex"
                    style={{
                      right: 8,
                      top: "50%",
                    }}
                    disabled={!question.trim() || isInputDisabled}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 320 512"
                      width="20"
                      height="20"
                    >
                      <defs>
                        <linearGradient
                          id="fa-custom-gradient"
                          x1="0%"
                          y1="0%"
                          x2="0%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#0091EA" />
                          <stop offset="100%" stopColor="#9B51E0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M313.6 119.7L209.6 7.7c-9.5-10.2-25.7-10.2-35.2 0L70.4 119.7C56.2 135 67.1 160 88 160H152v272H68a12 12 0 0 0 -8.5 3.5l-56 56C-4 499.1 1.3 512 12 512H208c13.3 0 24-10.7 24-24V160h64c20.9 0 31.9-25 17.6-40.3z"
                        fill={
                          isDarkTheme ? "#ffffff" : "url(#fa-custom-gradient)"
                        }
                      />
                    </svg>
                  </Button>
                </Flex>
              </form>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

AIChatV3Interface.propTypes = {
  variant: PropTypes.oneOf(["sidebar", "fullpage"]),
  showCloseButton: PropTypes.bool,
  onClose: PropTypes.func,
  onChatSelect: PropTypes.func,
  headerTitle: PropTypes.string,
  showChatHistoryToggle: PropTypes.bool,
  showNewChatButton: PropTypes.bool,
};

export default AIChatV3Interface;
